import Stats from 'frontend_stats';
import FullstoryUtil from 'fullstory_util';
import PersonalizeUtil from 'personalize_util';
import {
	getListingData,
	BrazeUtil,
	TrackUtil,
	HeapUtil,
	getCustomData,
	plural,
	trackFbEvent,
	parseTrackingParams,
} from 'core/utils';
import { default as ErrorTracker } from 'error_tracker';
import { updateUserAttributes, getItemProps } from 'core/utils/Braze';

function setupJustunoCallback() {
	if (!window?.juapp || typeof window.juapp !== 'function') return;

	// Justuno Impression Callback
	// Reference: https://support.justuno.com/en/passing-information-from-justuno-popup-to-your-site
	window.juapp('trackFunc', 'impression', (email, eventname, promotitle, cmid) => {
		Stats.trackGeneric('justuno_impression', {
			cus1: email,
			cus2: eventname,
			cus3: promotitle,
			cus4: cmid,
		});
		FullstoryUtil.sendEvent('Justuno impression', {
			title_str: `${promotitle}`,
			cmid_str: `${cmid}`,
		});
	});
}

function setupOptimizeCallback() {
	if (!window?.gtag) {
		return;
	}

	window.gtag('event', 'optimize.callback', {
		callback: (combination, experimentId, containerId) => {
			// Nice-to-have debugging & tracking for Optimize AB experiences
			console.debug(`optimize.callback for http://optimize.google.com/experiences/${experimentId}?containerId=${containerId}&combination=${combination}`);

			Stats.trackGeneric('a/b test init', {
				cus1: 'experiment:' + experimentId,
				cus2: 'cell:' + combination,
			});

			// Tag user in Heap:
			HeapUtil.track("Experiment Impression", {
				experiment: experimentId,
				variant: combination,
			});
		}
	});
}



export default {
	base() {
		// Tracking
		BrazeUtil.initialize();
		Stats.registerGaParams();
		// Expose TrackUtil for backbone to consume
		window.TrackUtil = TrackUtil;
		Stats.trackImpression('marketplace');
		/* Shared Across Templates */
		Stats.trackLinks(".listing-box a");
		Stats.trackLinks(".exit-url");

		setupOptimizeCallback();
		setupJustunoCallback();

		Stats.trackPageload();
		HeapUtil.identify();
		if (window?.cj?.user) {
			const { email, id, name } = window.cj.user;
			ErrorTracker.setUser({
				email: email,
				id: id,
			});

			FullstoryUtil.identify(
				id,
				name,
				email,
			);
		}
		FullstoryUtil.sendUrlToSentry();
	},

	trackQueryParams() {
		/*
		* Tracking query
		* `discovery`: event used to track buyers 'discovering' content (listings, collections, categories).
		* 		Used to get better insights on the effectiveness of merchandicing.
		* cus2: <pt> page_type:    assuming discovery url structure:
		* cus3: <gs> general_slug: `/<page_type>/<general_slug>`
		* cus4: <pn> page_number:  page within result set
		* cus5: <cn> card_number:  position within discovery page
		* cus6: <ft> filter_tags:  filter-group-tags applied to result set
		* cus7: <sn> section_name: section name card is displayed within
		*/
		const qParams = parseTrackingParams();
		if (qParams.pt || qParams.gs || qParams.cn || qParams.pn || qParams.ft || qParams.sn) {
			Stats.trackGeneric('discovery', {
				cus1: 'track',
				cus2: qParams.pt,
				cus3: qParams.gs,
				cus4: qParams.pn,
				cus5: qParams.cn,
				cus6: qParams.ft,
				cus7: qParams.sn,
			});
		}
	},

	listing() {
		const listing = getListingData();
		if (listing) {
			TrackUtil.trackEvent("product_view_exception", { braze: [] });
			TrackUtil.trackEvent("product_view", {
				braze: [getItemProps()],
			});

			// Handle personalize events & possible recommendation stats:
			PersonalizeUtil.onListingView(listing.listingId, listing.categories);

			trackFbEvent('ViewContent', {
				external_id: window.cj?.user?.id,
				content_type: 'product',
				content_ids: [listing.listingId],
			});

			updateUserAttributes({ boxesViewed: [listing.name] });
		}

		// Track the "in-house utm" params
		this.trackQueryParams();
	},

	checkout() {
		BrazeUtil.initialize();
		// Expose TrackUtil for backbone to consume
		window.TrackUtil = TrackUtil;
		window.BrazeUtil = BrazeUtil;
		// Checkout doesn't do `Analytics.base()`
		setupOptimizeCallback();
	},

	discovery() {
		this.trackQueryParams();
		if (! window) return;
		const pageType = window?.cj?.page_type;
		const trackable = pageType === "category" || pageType === "subcategory" || pageType === "collection";
		if (! trackable) return;
		const headerProps = getCustomData(window.cj.header_props, {});
		const slug = headerProps.bottomNavProps.currentSlug;
		const attributes = {};
		const key = `${plural(pageType)}Viewed`;
		attributes[key] = [slug];
		updateUserAttributes(attributes);
	}
};
